import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { FileText, Loader2, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TemplateSelectDialog = ({ open, onClose, onSelectTemplate, studentId }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [cloning, setCloning] = useState(null);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCloneToStudent = async (template) => {
    setCloning(template.id);
    try {
      const response = await axios.post(`${API}/rubrics/${template.id}/duplicate`, null, {
        params: { student_id: studentId }
      });
      onSelectTemplate(response.data);
      toast.success('Template added to student');
      onClose();
    } catch (err) {
      toast.error('Failed to clone template');
    } finally {
      setCloning(null);
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add from Template</DialogTitle>
        </DialogHeader>
        
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="template-search-input"
          />
        </div>

        {/* Templates List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1} />
              <p className="text-sm text-muted-foreground">No templates yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Create a rubric without a student to save it as a template
              </p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">No templates match "{searchQuery}"</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className="border border-border rounded-lg p-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-3">
                      <h4 className="font-medium text-sm">{template.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 bg-muted rounded-full">{template.category}</span>
                        <span className="text-xs text-muted-foreground">
                          {template.domains?.length || 0} domains
                        </span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleCloneToStudent(template)}
                      disabled={cloning === template.id}
                      data-testid={`clone-template-${template.id}`}
                    >
                      {cloning === template.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Use'
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
